import { useTranslation } from 'react-i18next'
import { parseCardtraderAttributeErrors } from 'shared'

interface TransactionsError {
  // several strings when the channel has reworded the same error over time
  errorMessage: string | string[]
  // key under transaction.lastErrors
  textKey: string
}

// first match wins; anything unmatched shows internalError
const cardmarketErrors: TransactionsError[] = [
  {
    errorMessage: 'This product does not exist as specified. Please try again.',
    textKey: 'wrongAttributes',
  },
  { errorMessage: 'articles not found on cardmarket', textKey: 'articlesNotFound' },
  {
    errorMessage:
      'ERROR: You have exceeded the maximal amount of articles you can put on sale for this product. The item has not been listed.',
    textKey: 'exceededCards',
  },
  { errorMessage: 'Cannot edit Article with ID ', textKey: 'cart' },
  {
    errorMessage:
      'The price you entered was not valid. The minimum price for an item is 0.02 Euros. Please try again.',
    textKey: 'lowPrice',
  },
  {
    errorMessage:
      'Failed to open TCP connection to apiv2.cardmarket.com:443 (execution expired). The socket took over 10 seconds to connect (on prod-shard-00-02.gkeus.mongodb.net:27017, connection 2:2, modern retry, attempt 2)',
    textKey: 'errorConnection',
  },
  {
    errorMessage: 'An error has ocurred, you are not allowed to sell',
    textKey: 'notAllowedSell',
  },
  { errorMessage: 'Cardmarket is down for maintenance', textKey: 'cardmarketMaintenance' },
  {
    // Whoops! You don't have pre-sale privileges for Magic products with pre-releases.
    errorMessage: "Whoops! You don't have pre-sale privileges for",
    textKey: 'preReleaseSales',
  },
  {
    errorMessage: [
      'You have exceeded the maximal amount of presale articles you can put on sale. The item has not been listed',
      'You have exceeded the maximum number of presale articles you can list',
    ],
    textKey: 'presaleMaximumAmount',
  },
  {
    errorMessage:
      'The item has NOT been listed. As a private seller you are not allowed to sell presale products.',
    textKey: 'privateSellerPresale',
  },
  { errorMessage: 'No matching articles found', textKey: 'noArticleFound' },
  {
    errorMessage: 'Your comment was too long or included not allowed characters',
    textKey: 'commentNotAllowed',
  },
  { errorMessage: 'Comments may not be longer than 100 characters', textKey: 'commentTooLong' },
  // followed by the user id and cardmarketUsername, which vary
  { errorMessage: 'No active Cardmarket account for user', textKey: 'cardmarketNotConnected' },
]

const cardtraderErrors: TransactionsError[] = [
  // any attribute (foil, language, first_edition, reverse...)
  {
    errorMessage: ['accepts a value within', 'is not editable. You specified'],
    textKey: 'cardtraderAttributeMismatch',
  },
  {
    errorMessage: ['Blueprint with ID', 'Cardtrader handle not found'],
    textKey: 'cardtraderProductNotFound',
  },
  // every 5xx: Cloudflare pages, maintenance, internal server errors
  { errorMessage: 'HTTP response error with status 5', textKey: 'cardtraderUnavailable' },
  { errorMessage: 'user cannot do presales', textKey: 'cardtraderPresale' },
  { errorMessage: 'Listing basic lands is disabled', textKey: 'cardtraderBasicLandsDisabled' },
  { errorMessage: 'Listing sealed products is disabled', textKey: 'cardtraderSealedDisabled' },
  {
    errorMessage: 'Listing products for this game is disabled',
    textKey: 'cardtraderGameDisabled',
  },
  { errorMessage: '"price_cents"=>', textKey: 'cardtraderPriceTooHigh' },
  {
    errorMessage: 'HTTP response error with status 401: ""',
    textKey: 'cardtraderNotConnected',
  },
]

export const getErrorContent = (
  error: string,
  gameName: string,
  mkmProductId: number,
  channel: string
) => {
  // channel is Rails' platform label (I18n platforms.cardtrader)
  const errors = channel === 'CardTrader' ? cardtraderErrors : cardmarketErrors
  const textKey =
    errors.find(({ errorMessage }) =>
      ([] as string[]).concat(errorMessage).some((message) => error.includes(message))
    )?.textKey ?? 'internalError'

  return (
    <ErrorDialogContent
      textKey={textKey}
      rawError={error}
      gameName={gameName}
      mkmProductId={mkmProductId}
    />
  )
}

interface ErrorDialogContentProps {
  textKey: string
  rawError: string
  gameName: string
  mkmProductId: number
}

const ErrorDialogContent: React.FC<ErrorDialogContentProps> = ({
  textKey,
  rawError,
  gameName,
  mkmProductId,
}) => {
  const { t } = useTranslation()

  if (textKey === 'wrongAttributes')
    return (
      <div>
        <h3>{t('transaction.lastErrors.wrongAttributes')}</h3>
        <p>{t('transaction.lastErrors.wrongAttributesExplain')}</p>
        {!!mkmProductId && (
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://www.cardmarket.com/en/${gameName}/Products?idProduct=${mkmProductId}`}
          >
            CARDMARKET PRODUCT
          </a>
        )}
        <p className="mt-2">{t('transaction.lastErrors.wrongAttributesFix')}</p>
      </div>
    )

  if (textKey === 'cardtraderAttributeMismatch') {
    const issues = parseCardtraderAttributeErrors(rawError)
    return (
      <div>
        <h3>{t('transaction.lastErrors.cardtraderAttributeMismatch')}</h3>
        <p>{t('transaction.lastErrors.cardtraderAttributeMismatchExplain')}</p>
        {issues.length > 0 && (
          <ul className="mt-2">
            {issues.map((issue, idx) => (
              <li key={idx}>
                <strong>{issue.attribute}</strong>: {t('transaction.lastErrors.youSent')}{' '}
                <code>{issue.submitted}</code>, {t('transaction.lastErrors.cardtraderExpects')}{' '}
                {issue.expected.map((v, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    <code>{v}</code>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2">{t('transaction.lastErrors.cardtraderAttributeMismatchFix')}</p>
      </div>
    )
  }

  return <div>{t(`transaction.lastErrors.${textKey}`)}</div>
}
